import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import AttachmentManager from '../../components/kb/AttachmentManager';
import WinningProposalsCard from '../../components/kb/WinningProposalsCard';
import ClientSentinelCard from '../../components/sentinel/ClientSentinelCard';
import EbuyProfilePanel from '../../components/sentinel/EbuyProfilePanel';
import {
  CertificationFormModal,
  KeyPersonnelFormModal,
  PastPerformanceFormModal,
} from '../../components/kb/ClientProfileForms';
import {
  Building2,
  MapPin,
  Globe,
  Mail,
  Phone,
  Pencil,
  Plus,
  Trash2,
  Users,
  FileText,
  Shield,
  Award,
  CheckCircle2,
  Linkedin,
  ChevronLeft,
  Briefcase,
  Calendar,
  ExternalLink,
  UserCheck,
  Target,
  Clock,
  Loader2,
  Save,
  X,
  BookOpen,
  Sparkles,
  Palette,
  Download,
  Eye,
  Bot,
  Zap,
  BrainCircuit,
  Settings2,
  Library,
  Plug,
  KeyRound,
  Upload,
  Image as ImageIcon,
  FilePlus2,
  EyeOff,
  Link2,
  Hash,
  CalendarDays,
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import PromoteToLibraryButton from '../../components/consultant/PromoteToLibraryButton';
import KbDocumentsDropzone from '../../components/kb/KbDocumentsDropzone';
import ExtractionStatusBadge from '../../components/kb/ExtractionStatusBadge';

// API Base URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Socio-economic tag colors
const getSocioEconomicBadgeStyle = (tag) => {
  const styles = {
    'WOSB': 'bg-rose-100 text-rose-700 border-rose-200',
    'SBA 8(a)': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    '8(a)': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    '8(a) Small Business': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'HUBZone': 'bg-blue-100 text-blue-700 border-blue-200',
    'SDVOSB': 'bg-purple-100 text-purple-700 border-purple-200',
    'VOSB': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Small Business': 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return styles[tag] || 'bg-slate-100 text-slate-700 border-slate-200';
};

// Past performance rating colors
const getRatingBadgeStyle = (rating) => {
  const styles = {
    'EXCEPTIONAL': 'bg-emerald-100 text-emerald-700',
    'VERY_GOOD': 'bg-blue-100 text-blue-700',
    'SATISFACTORY': 'bg-amber-100 text-amber-700',
    'MARGINAL': 'bg-orange-100 text-orange-700',
    'UNSATISFACTORY': 'bg-red-100 text-red-700',
    'NOT_RATED': 'bg-slate-100 text-slate-500',
  };
  return styles[rating] || 'bg-slate-100 text-slate-700';
};

const formatCparsRating = (rating) => {
  const mapping = {
    'EXCEPTIONAL': 'Exceptional',
    'VERY_GOOD': 'Very Good',
    'SATISFACTORY': 'Satisfactory',
    'MARGINAL': 'Marginal',
    'UNSATISFACTORY': 'Unsatisfactory',
    'NOT_RATED': 'N/A',
  };
  return mapping[rating] || rating;
};

const formatCurrency = (value) => {
  if (!value) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};

export default function ClientProfile() {
  const { workspaceId, clientId } = useParams();
  const navigate = useNavigate();
  const { user, authToken } = useAuth();
  
  // State
  const [client, setClient] = useState(null);
  const [pastPerformances, setPastPerformances] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [keyPersonnel, setKeyPersonnel] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [editForm, setEditForm] = useState({});

  // v3.11.27 — CRUD parity modals for the 3 legacy KB cards
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certEditing, setCertEditing] = useState(null);
  const [kpModalOpen, setKpModalOpen] = useState(false);
  const [kpEditing, setKpEditing] = useState(null);
  const [ppModalOpen, setPpModalOpen] = useState(false);
  const [ppEditing, setPpEditing] = useState(null);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  // PP detail panel
  const [ppDetailOpen, setPpDetailOpen] = useState(false);
  const [ppDetailItem, setPpDetailItem] = useState(null);
  const [ppAttachments, setPpAttachments] = useState([]);
  const [ppAttachmentsLoading, setPpAttachmentsLoading] = useState(false);

  // Certification detail panel
  const [certDetailOpen, setCertDetailOpen] = useState(false);
  const [certDetailItem, setCertDetailItem] = useState(null);
  const [certDetailAttachments, setCertDetailAttachments] = useState([]);
  const [certDetailLoading, setCertDetailLoading] = useState(false);

  // Key Personnel detail panel
  const [kpDetailOpen, setKpDetailOpen] = useState(false);
  const [kpDetailItem, setKpDetailItem] = useState(null);
  const [kpDetailAttachments, setKpDetailAttachments] = useState([]);
  const [kpDetailLoading, setKpDetailLoading] = useState(false);

  // AI Models tab
  const [aiProviders, setAiProviders] = useState(null);
  const [aiConfig, setAiConfig] = useState({ primary: { provider: '', model: '' }, secondary: { provider: '', model: '' } });
  const [aiConfigSaving, setAiConfigSaving] = useState(false);

  // Per-client API keys (typed values — masked stored keys from DB show as "•••…4abc")
  const [apiKeyInputs, setApiKeyInputs] = useState({ anthropic: '', openai: '', gemini: '' });
  const [apiKeysVisible, setApiKeysVisible] = useState({ anthropic: false, openai: false, gemini: false });
  const [apiKeysSaving, setApiKeysSaving] = useState(false);

  // Logo + capability statement upload (Overview)
  const [logoUploading, setLogoUploading] = useState(false);
  const [capStatUploading, setCapStatUploading] = useState(false);
  const logoInputRef = useRef(null);
  const capStatInputRef = useRef(null);

  // v3.18.33 — eLibrary import: bump key to force EbuyProfilePanel remount after apply
  const [ebuyReloadKey, setEbuyReloadKey] = useState(0);

  // HigherGov 3rd-party integration config
  const [hgConfig, setHgConfig] = useState({ api_key: '', searches: [{ label: '', search_id: '', scope: '' }], posted_days: '', naics_codes: [] });
  const [hgNaicsInput, setHgNaicsInput] = useState('');
  const [hgApiKeyVisible, setHgApiKeyVisible] = useState(false);
  const [hgSaving, setHgSaving] = useState(false);

  // Open Certification detail panel
  const handleOpenCertDetail = async (cert) => {
    setCertDetailItem(cert);
    setCertDetailOpen(true);
    setCertDetailAttachments([]);
    setCertDetailLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/certifications/${cert.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCertDetailAttachments(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load cert attachments:', err);
    } finally {
      setCertDetailLoading(false);
    }
  };

  // Open Key Personnel detail panel
  const handleOpenKpDetail = async (person) => {
    setKpDetailItem(person);
    setKpDetailOpen(true);
    setKpDetailAttachments([]);
    setKpDetailLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/key-personnel/${person.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setKpDetailAttachments(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load KP attachments:', err);
    } finally {
      setKpDetailLoading(false);
    }
  };

  // Generic KB attachment download
  const downloadKbAttachment = async (att) => {
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/file/${att.id}/download`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Download failed: ' + err.message);
    }
  };

  // Load AI providers on mount; seed aiConfig from client data
  useEffect(() => {
    if (!authToken) return;
    fetch(`${API_URL}/api/llm/providers`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.providers) setAiProviders(data.providers); })
      .catch(() => {});
  }, [authToken]);

  // Seed aiConfig + apiKeyInputs from client once loaded
  useEffect(() => {
    if (!client) return;
    const saved = client.client_ai_config;
    if (saved) {
      setAiConfig({
        primary:   saved.primary   || { provider: '', model: '' },
        secondary: saved.secondary || { provider: '', model: '' },
      });
    }
    // Seed API key inputs with masked values returned by the backend
    // (so users see "•••…4abc" for keys that are already set)
    const savedKeys = client.client_api_keys || {};
    setApiKeyInputs({
      anthropic: savedKeys.anthropic || '',
      openai:    savedKeys.openai    || '',
      gemini:    savedKeys.gemini    || '',
    });

    // Seed HigherGov integration config
    const hg = client.third_party_integrations?.highergov || {};
    const hgCodes = hg.naics_codes || [];
    const savedSearches = (hg.searches || []).length > 0
      ? hg.searches
      : [{ label: '', search_id: '', scope: '' }];
    // posted_days is canonical; fall back to converting a legacy posted_months value for display.
    const postedDays = hg.posted_days != null
      ? hg.posted_days
      : (hg.posted_months != null ? hg.posted_months * 30 : null);
    setHgConfig({
      api_key:       savedKeys.highergov || '',
      searches:      savedSearches,
      posted_days:   postedDays != null ? String(postedDays) : '',
      naics_codes:   hgCodes,
    });
    setHgNaicsInput(hgCodes.join(', '));
  }, [client]);

  const handleAiProviderChange = (slot, provider) => {
    const firstModel = aiProviders?.[provider]?.models?.[0]?.id || '';
    setAiConfig(prev => ({ ...prev, [slot]: { provider, model: firstModel } }));
  };

  const handleSaveAiConfig = async () => {
    setAiConfigSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ client_ai_config: aiConfig }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('AI model preferences saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setAiConfigSaving(false);
    }
  };

  // Save client-owned API keys
  const handleSaveApiKeys = async () => {
    setApiKeysSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ client_api_keys: apiKeyInputs }),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      // Refresh masked values from the backend response
      const maskedKeys = updated.client_api_keys || {};
      setApiKeyInputs({
        anthropic: maskedKeys.anthropic || '',
        openai:    maskedKeys.openai    || '',
        gemini:    maskedKeys.gemini    || '',
      });
      toast.success('API keys saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setApiKeysSaving(false);
    }
  };

  // Save HigherGov integration config
  const handleSaveHigherGov = async () => {
    const validSearches = hgConfig.searches.filter(s => s.search_id.trim());
    if (validSearches.length === 0) {
      toast.error('At least one Search ID is required');
      return;
    }
    setHgSaving(true);
    try {
      const naicsCodes = hgNaicsInput.split(',').map(s => s.trim()).filter(Boolean);
      const hgIntegration = {
        searches: validSearches.map(s => ({
          label: s.label.trim(),
          search_id: s.search_id.trim(),
          ...(s.scope ? { scope: s.scope } : {}),
        })),
      };
      const days = parseInt(hgConfig.posted_days);
      if (!isNaN(days) && days > 0) hgIntegration.posted_days = days;
      if (naicsCodes.length) hgIntegration.naics_codes = naicsCodes;

      const res = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          client_api_keys: { highergov: hgConfig.api_key },
          third_party_integrations: { highergov: hgIntegration },
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      const maskedKey = updated.client_api_keys?.highergov || '';
      setHgConfig(prev => ({ ...prev, api_key: maskedKey }));
      toast.success('HigherGov integration saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setHgSaving(false);
    }
  };

  // Upload client logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/clients/${clientId}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success('Logo uploaded');
      fetchClientData(); // refresh client to get new logo_gridfs_id
    } catch (err) {
      toast.error(err.message || 'Logo upload failed');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  // Upload capability statement
  const handleCapStatUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapStatUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/clients/${clientId}/capability-statement`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success('Capability statement uploaded');
      fetchClientData();
    } catch (err) {
      toast.error(err.message || 'Capability statement upload failed');
    } finally {
      setCapStatUploading(false);
      if (capStatInputRef.current) capStatInputRef.current.value = '';
    }
  };

  // Download capability statement
  const handleDownloadCapStat = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/capability-statement`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const nameMatch = cd.match(/filename="([^"]+)"/);
      const filename = nameMatch?.[1] || client?.cap_statement_filename || 'capability_statement';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (err) {
      toast.error('Download failed: ' + err.message);
    }
  };

  // Open PP detail panel: load attachments then show modal
  const handleOpenPpDetail = async (project) => {
    setPpDetailItem(project);
    setPpDetailOpen(true);
    setPpAttachments([]);
    setPpAttachmentsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/past-performances/${project.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setPpAttachments(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load PP attachments:', err);
    } finally {
      setPpAttachmentsLoading(false);
    }
  };

  // Download a KB attachment file via GridFS stream
  const downloadPpAttachment = async (att) => {
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/file/${att.id}/download`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Download failed: ' + err.message);
    }
  };

  // Generic delete helper for KB entities (certifications, key-personnel, past-performances)
  const deleteKbEntity = async (domain, id, label) => {
    if (!window.confirm(`Delete this ${label}?\n\nThis also removes any attachments.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/knowledge-base/${domain}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success(`${label} removed`);
      fetchClientData();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  // Fetch client data
  const fetchClientData = async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    try {
      // Fetch client details
      const clientRes = await fetch(`${API_URL}/api/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!clientRes.ok) throw new Error('Failed to fetch client');
      const clientData = await clientRes.json();
      setClient(clientData);
      setEditForm(clientData);
      
      // Fetch past performances
      const ppRes = await fetch(`${API_URL}/api/knowledge-base/past-performances?organization_id=${clientId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (ppRes.ok) {
        const ppData = await ppRes.json();
        setPastPerformances(ppData.items || []);
      }
      
      // Fetch certifications
      const certRes = await fetch(`${API_URL}/api/knowledge-base/certifications?organization_id=${clientId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (certRes.ok) {
        const certData = await certRes.json();
        setCertifications(certData.items || []);
      }
      
      // Fetch key personnel
      const kpRes = await fetch(`${API_URL}/api/knowledge-base/key-personnel?organization_id=${clientId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (kpRes.ok) {
        const kpData = await kpRes.json();
        setKeyPersonnel(kpData.items || []);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
      toast.error('Failed to load client data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  // v3.19.0 — Documents-First: poll for extraction status when any row is
  // still being processed. Backs off after 30 polls (~2.5 min) to avoid
  // runaway intervals if a worker hung.
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  useEffect(() => {
    const anyExtracting = [
      ...pastPerformances,
      ...certifications,
      ...keyPersonnel,
    ].some((r) => r.extraction_status === 'pending' || r.extraction_status === 'running');

    if (!anyExtracting) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        pollCountRef.current = 0;
      }
      return;
    }
    if (pollRef.current) return; // already polling
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current > 30) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        return;
      }
      fetchClientData();
    }, 5000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pastPerformances, certifications, keyPersonnel]);

  // Handle form field change
  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Analyze website — extract brand colors + capabilities via AI
  const handleScanWebsite = async () => {
    const url = (editForm.website || '').trim();
    if (!url) {
      toast.error('Enter the website URL first.');
      return;
    }
    setIsScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/scan-website`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEditForm(prev => ({
        ...prev,
        brand_colors: data.brand_colors || prev.brand_colors,
        capabilities_summary: data.capabilities_summary || prev.capabilities_summary,
      }));
      toast.success('Brand scan complete — review colors & capabilities below.');
    } catch (err) {
      toast.error(`Scan failed: ${err.message}`);
    } finally {
      setIsScanning(false);
    }
  };

  // Save client changes
  const handleSaveClient = async () => {
    if (!(editForm.website || '').trim()) {
      toast.error('Website URL is required.');
      return;
    }
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          name: editForm.name,
          uei: editForm.uei || null,
          cage_code: editForm.cage_code || null,
          duns: editForm.duns || null,
          website: editForm.website || null,
          phone: editForm.phone || null,
          address_line1: editForm.address_line1 || null,
          address_line2: editForm.address_line2 || null,
          city: editForm.city || null,
          state: editForm.state || null,
          zip_code: editForm.zip_code || null,
          business_size: editForm.business_size || null,
          primary_naics: editForm.primary_naics || null,
          industry_sector: editForm.industry_sector || null,
          ebuy_client_code: (editForm.ebuy_client_code || '').trim().toUpperCase() || null,
          brand_colors: editForm.brand_colors || null,
          capabilities_summary: editForm.capabilities_summary || null,
        })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to update client');
      }
      
      const updatedClient = await response.json();
      setClient(updatedClient);
      setIsEditModalOpen(false);
      toast.success('Client profile updated successfully');
    } catch (err) {
      console.error('Error updating client:', err);
      toast.error(err.message || 'Failed to update client');
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading client profile...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!client) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <p className="text-red-700">Client not found</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate(`/${workspaceId}/clients`)}
            >
              Back to Directory
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const location = client.city && client.state 
    ? `${client.city}, ${client.state}` 
    : client.city || client.state || 'Location not set';

  return (
    <div data-testid="client-profile-page" className="min-h-screen bg-slate-50">
      {/* Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <button 
                onClick={() => navigate(`/${workspaceId}/clients`)}
                className="hover:text-blue-600 flex items-center gap-1 transition-colors"
                data-testid="back-to-directory-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Client Directory
              </button>
            </div>
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Client Profile
            </h1>
          </div>
          <Button 
            variant="outline" 
            className="border-slate-200"
            onClick={() => setIsEditModalOpen(true)}
            data-testid="edit-profile-btn"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Client Header Card */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              {/* Logo/Icon */}
              <div className="w-20 h-20 bg-blue-50 rounded-xl border-2 border-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden relative group">
                {client.logo_gridfs_id ? (
                  <img
                    src={`${API_URL}/api/clients/${clientId}/logo`}
                    alt={`${client.name} logo`}
                    className="w-full h-full object-contain p-1"
                    style={{ imageRendering: 'crisp-edges' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Building2 className="w-10 h-10 text-blue-500" />
                )}
              </div>
              
              <div className="flex-1">
                {/* Company Name */}
                <h2 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {client.name}
                </h2>
                
                {/* Slug */}
                <p className="text-sm text-slate-500 mb-3">
                  Slug: {client.slug}
                </p>
                
                {/* Location & Status */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {location}
                  </div>
                  <span className="text-slate-300">|</span>
                  <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-200 font-medium">
                    Active Client
                  </Badge>
                  {client.business_size && (
                    <Badge className={cn("border font-medium", getSocioEconomicBadgeStyle(client.business_size))}>
                      {client.business_size}
                    </Badge>
                  )}
                </div>
                
                {/* Tags Row */}
                <div className="flex items-center gap-2 flex-wrap">
                  {client.industry_sector && (
                    <Badge className="bg-blue-50 text-blue-600 border border-blue-200">
                      {client.industry_sector}
                    </Badge>
                  )}
                  {client.primary_naics && (
                    <Badge variant="outline" className="text-slate-600 border-slate-200">
                      NAICS: {client.primary_naics}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabbed layout */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-slate-200 shadow-sm h-10 p-1">
            <TabsTrigger value="overview" className="flex items-center gap-1.5 text-sm">
              <FileText className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="library" className="flex items-center gap-1.5 text-sm" data-testid="client-library-tab">
              <Library className="w-4 h-4" />
              Client Library
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-1.5 text-sm">
              <Plug className="w-4 h-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="ai-models" className="flex items-center gap-1.5 text-sm">
              <Bot className="w-4 h-4" />
              AI Models
            </TabsTrigger>
            <TabsTrigger value="3rd-party" className="flex items-center gap-1.5 text-sm">
              <Link2 className="w-4 h-4" />
              3rd Party
            </TabsTrigger>
          </TabsList>

          {/* ── Overview Tab ── */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <FileText className="w-5 h-5 text-blue-600" />
                  Organization Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Legal Name</p>
                    <p className="text-sm font-medium text-slate-900">{client.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Business Size</p>
                    <p className="text-sm font-medium text-slate-900">{client.business_size || 'Not specified'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">UEI (Unique Entity ID)</p>
                    <p className="text-sm font-mono font-medium text-slate-900">{client.uei || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">CAGE Code</p>
                    <p className="text-sm font-mono font-medium text-slate-900">{client.cage_code || 'N/A'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Primary NAICS</p>
                    <p className="text-sm font-mono font-medium text-slate-900">{client.primary_naics || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Industry Sector</p>
                    <p className="text-sm font-medium text-slate-900">{client.industry_sector || 'Not specified'}</p>
                  </div>
                </div>
                <Separator className="bg-slate-100" />
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Address</p>
                  <p className="text-sm text-slate-900">
                    {client.address_line1 ? (
                      <>
                        {client.address_line1}<br/>
                        {client.address_line2 && <>{client.address_line2}<br/></>}
                        {client.city && `${client.city}, `}{client.state} {client.zip_code}
                      </>
                    ) : 'Not specified'}
                  </p>
                </div>
                {client.website && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Corporate Website</p>
                    <a
                      href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                    >
                      {client.website}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {client.phone && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-sm text-slate-900">{client.phone}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Brand palette + capabilities summary */}
            {(client.brand_colors?.primary || client.capabilities_summary) && (
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    AI Brand Intelligence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {client.brand_colors?.primary && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                        Brand Palette{client.brand_colors.palette_name ? ` — ${client.brand_colors.palette_name}` : ''}
                      </p>
                      <div className="flex items-center gap-3 flex-wrap">
                        {['primary','secondary','accent','background','text'].map(key => {
                          const hex = client.brand_colors?.[key];
                          if (!hex) return null;
                          return (
                            <div key={key} className="flex flex-col items-center gap-1">
                              <div className="w-9 h-9 rounded-md border border-slate-200 shadow-sm" style={{ background: hex }} title={hex} />
                              <span className="text-[10px] text-slate-500 uppercase">{key}</span>
                              <span className="text-[10px] text-slate-400 font-mono">{hex}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {client.capabilities_summary && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Capabilities Summary</p>
                      <p className="text-sm text-slate-700 leading-relaxed">{client.capabilities_summary}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Assets: Logo + Capability Statement */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Upload className="w-4 h-4 text-blue-600" />
                  Company Assets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Logo upload */}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" /> Company Logo
                  </p>
                  <div className="flex items-center gap-3">
                    {client.logo_gridfs_id ? (
                      <div className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                        <img src={`${API_URL}/api/clients/${clientId}/logo`} alt="Logo" className="w-full h-full object-contain p-1" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                    <div className="space-y-1">
                      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                      <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5"
                        onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
                        {logoUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {client.logo_gridfs_id ? 'Replace Logo' : 'Upload Logo'}
                      </Button>
                      <p className="text-[11px] text-slate-400">PNG, JPG, SVG — max 5 MB</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Capability statement upload */}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FilePlus2 className="w-3.5 h-3.5" /> Capability Statement
                    {!client.cap_statement_gridfs_id && (
                      <span className="font-normal text-slate-400 normal-case">— upload your latest brochure/capabilities deck</span>
                    )}
                  </p>
                  {client.cap_statement_gridfs_id ? (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{client.cap_statement_filename || 'Capability Statement'}</p>
                        {client.cap_statement_updated_at && (
                          <p className="text-xs text-slate-400">Uploaded {new Date(client.cap_statement_updated_at).toLocaleDateString()}</p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" className="text-xs h-8 gap-1 shrink-0" onClick={handleDownloadCapStat}>
                        <Download className="w-3 h-3" /> Download
                      </Button>
                    </div>
                  ) : null}
                  <div className="mt-2">
                    <input ref={capStatInputRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" className="hidden" onChange={handleCapStatUpload} />
                    <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5"
                      onClick={() => capStatInputRef.current?.click()} disabled={capStatUploading}>
                      {capStatUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FilePlus2 className="w-3 h-3" />}
                      {client.cap_statement_gridfs_id ? 'Replace Statement' : 'Upload Capability Statement'}
                    </Button>
                    <span className="ml-2 text-[11px] text-slate-400">PDF, DOCX, PPTX</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Client Library Tab ── */}
          <TabsContent value="library" className="mt-6 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-5 h-5 text-teal-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="client-library-heading">
                  Client Library
                </h2>
              </div>
              <p className="text-xs text-slate-500">
                Federal-grade structured evidence the pursuit AI pulls into capability statements and past-performance matching.
                Click any row to view extracted details and download uploaded files.
              </p>
            </div>

            {/* Certifications */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Shield className="w-5 h-5 text-blue-600" />
                    Certifications ({certifications.length})
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => { setCertEditing(null); setCertModalOpen(true); }} data-testid="add-certification-btn" className="text-xs h-8">
                    <Plus className="w-3.5 h-3.5 mr-1" />Manual
                  </Button>
                </div>
                <div className="mt-3">
                  <KbDocumentsDropzone entityType="certifications" organizationId={clientId} onUploaded={() => fetchClientData()} testIdPrefix="cert-dropzone" />
                </div>
              </CardHeader>
              <CardContent>
                {certifications.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No certifications recorded</p>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
                    {certifications.map((cert) => (
                      <div
                        key={cert.id}
                        className="flex items-center gap-2 p-2.5 bg-slate-50 hover:bg-blue-50 rounded border border-slate-100 cursor-pointer transition-colors"
                        data-testid={`certification-row-${cert.id}`}
                        onClick={() => handleOpenCertDetail(cert)}
                      >
                        <Award className={cn("w-4 h-4 flex-shrink-0", cert.status === 'ACTIVE' ? 'text-emerald-600' : cert.status === 'EXPIRED' ? 'text-red-600' : 'text-amber-600')} />
                        <span className="flex-1 text-sm font-medium text-slate-900 truncate">{cert.name}</span>
                        <ExtractionStatusBadge status={cert.extraction_status} error={cert.extraction_error} />
                        <Badge variant="outline" className={cn("text-xs flex-shrink-0", cert.status === 'ACTIVE' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : cert.status === 'EXPIRED' ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700')}>
                          {cert.status || 'PENDING'}
                        </Badge>
                        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <AttachmentManager itemType="certifications" itemId={cert.id} />
                          <PromoteToLibraryButton kind="certification" recordId={cert.id} defaultTitle={cert.name} />
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => { setCertEditing(cert); setCertModalOpen(true); }} data-testid={`edit-certification-${cert.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => deleteKbEntity('certifications', cert.id, 'certification')} data-testid={`delete-certification-${cert.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Key Personnel */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Users className="w-5 h-5 text-blue-600" />
                    Key Personnel ({keyPersonnel.length})
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => { setKpEditing(null); setKpModalOpen(true); }} data-testid="add-key-personnel-btn" className="text-xs h-8">
                    <Plus className="w-3.5 h-3.5 mr-1" />Manual
                  </Button>
                </div>
                <div className="mt-3">
                  <KbDocumentsDropzone entityType="key-personnel" organizationId={clientId} onUploaded={() => fetchClientData()} label="Drop resumes or click to upload — AI will fill in name, title, clearance, skills" testIdPrefix="kp-dropzone" />
                </div>
              </CardHeader>
              <CardContent>
                {keyPersonnel.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No key personnel recorded</p>
                ) : (
                  <div className="max-h-[480px] overflow-y-auto space-y-2 pr-1">
                    {keyPersonnel.map((person) => (
                      <div
                        key={person.id}
                        className="p-4 bg-slate-50 hover:bg-blue-50 rounded-lg border border-slate-100 cursor-pointer transition-colors"
                        data-testid={`key-personnel-row-${person.id}`}
                        onClick={() => handleOpenKpDetail(person)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-slate-600">{(person.first_name || '?')[0]}{(person.last_name || '?')[0]}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                                <span className="truncate">{person.first_name} {person.last_name}</span>
                                <ExtractionStatusBadge status={person.extraction_status} error={person.extraction_error} />
                              </h4>
                              <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                                <AttachmentManager itemType="key-personnel" itemId={person.id} />
                                <PromoteToLibraryButton kind="key-personnel" recordId={person.id} defaultTitle={`${person.first_name || ''} ${person.last_name || ''}`.trim()} />
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => { setKpEditing(person); setKpModalOpen(true); }} data-testid={`edit-key-personnel-${person.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => deleteKbEntity('key-personnel', person.id, 'team member')} data-testid={`delete-key-personnel-${person.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mb-1.5">{person.title || 'No title'}{person.years_experience ? ` · ${person.years_experience} yrs` : ''}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {person.clearance_level && person.clearance_level !== 'NONE' && (
                                <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-xs"><Shield className="w-3 h-3 mr-1" />{person.clearance_level.replace('_', ' ')}</Badge>
                              )}
                              {person.professional_certifications?.slice(0, 2).map((cert, i) => (
                                <Badge key={i} className="bg-green-50 text-green-700 border border-green-200 text-xs">{cert}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Past Performance */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    Past Performance ({pastPerformances.length})
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => { setPpEditing(null); setPpModalOpen(true); }} data-testid="add-past-performance-btn" className="text-xs h-8">
                    <Plus className="w-3.5 h-3.5 mr-1" />Manual
                  </Button>
                </div>
                <div className="mt-3">
                  <KbDocumentsDropzone entityType="past-performances" organizationId={clientId} onUploaded={() => fetchClientData()} label="Drop PP files or click to upload — AI will fill in agency, value, NAICS, CPARS, etc." testIdPrefix="pp-dropzone" />
                </div>
              </CardHeader>
              <CardContent>
                {pastPerformances.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No past performance recorded</p>
                ) : (
                  <div className="max-h-[480px] overflow-y-auto space-y-2 pr-1">
                    {pastPerformances.map((project) => (
                      <div
                        key={project.id}
                        className="p-4 bg-slate-50 hover:bg-blue-50 rounded-lg border border-slate-100 cursor-pointer transition-colors"
                        data-testid={`past-performance-row-${project.id}`}
                        onClick={() => handleOpenPpDetail(project)}
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-slate-900 text-sm flex items-center gap-2">
                              <span className="truncate">{project.contract_title}</span>
                              <ExtractionStatusBadge status={project.extraction_status} error={project.extraction_error} />
                            </h4>
                            <p className="text-xs text-slate-500">{project.client_agency}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Badge className={cn("text-xs", getRatingBadgeStyle(project.cpars_rating))}>{formatCparsRating(project.cpars_rating)}</Badge>
                            <AttachmentManager itemType="past-performances" itemId={project.id} />
                            <PromoteToLibraryButton kind="past-performance" recordId={project.id} defaultTitle={project.contract_title || ''} />
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => { setPpEditing(project); setPpModalOpen(true); }} data-testid={`edit-past-performance-${project.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => deleteKbEntity('past-performances', project.id, 'past performance')} data-testid={`delete-past-performance-${project.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Target className="w-3 h-3" />{formatCurrency(project.contract_value)}</span>
                          {(project.pop_start || project.pop_end) && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {project.pop_start && new Date(project.pop_start).getFullYear()} – {project.is_ongoing ? 'Present' : (project.pop_end && new Date(project.pop_end).getFullYear())}
                            </span>
                          )}
                          <Badge variant="outline" className={cn("text-xs", project.is_ongoing ? 'border-emerald-200 text-emerald-600' : 'border-slate-200 text-slate-500')}>
                            {project.is_ongoing ? 'Active' : 'Completed'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Winning Proposals */}
            <WinningProposalsCard organizationId={clientId} />
          </TabsContent>

          {/* ── Integrations Tab ── */}
          <TabsContent value="integrations" className="mt-6 space-y-6">
            {/* v3.18.33 — onElibraryApplied bumps ebuyReloadKey so EbuyProfilePanel remounts */}
            <ClientSentinelCard
              clientId={client.id}
              clientName={client.name}
              onElibraryApplied={() => setEbuyReloadKey((k) => k + 1)}
            />
            <EbuyProfilePanel
              key={ebuyReloadKey}
              organizationId={client.id}
              clientName={client.name}
            />
          </TabsContent>

          {/* ── AI Models Tab ── */}
          <TabsContent value="ai-models" className="mt-6 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Bot className="w-5 h-5 text-violet-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Model Preferences</h2>
              </div>
              <p className="text-xs text-slate-500">
                Set a <strong>Primary</strong> (fast, low-latency) model for everyday tasks like chat and classification,
                and a <strong>Secondary</strong> (adaptive, extended-thinking) model for complex work like proposal drafting and strategy analysis.
                These preferences override the workspace default for this client.
              </p>
            </div>

            {!aiProviders ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading available models…
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Primary model */}
                <Card className="border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Zap className="w-4 h-4 text-amber-500" />
                      Primary Model
                      <span className="ml-auto text-xs font-normal text-slate-400">Fast · Low-latency</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Provider</Label>
                      <Select value={aiConfig.primary.provider} onValueChange={(v) => handleAiProviderChange('primary', v)}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select provider…" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(aiProviders).map(([key, p]) => (
                            <SelectItem key={key} value={key}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {aiConfig.primary.provider && (
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Model</Label>
                        <Select value={aiConfig.primary.model} onValueChange={(v) => setAiConfig(prev => ({ ...prev, primary: { ...prev.primary, model: v } }))}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select model…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(aiProviders[aiConfig.primary.provider]?.models || []).map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                <span className="flex items-center gap-2">
                                  {m.name}
                                  {m.supports_thinking && <Badge className="text-[10px] h-4 bg-violet-100 text-violet-700 border-0">thinking</Badge>}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {aiConfig.primary.model && (
                      <p className="text-xs text-slate-400 font-mono bg-slate-50 rounded px-2 py-1">{aiConfig.primary.model}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Secondary model */}
                <Card className="border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <BrainCircuit className="w-4 h-4 text-violet-600" />
                      Secondary Model
                      <span className="ml-auto text-xs font-normal text-slate-400">Adaptive · Deep reasoning</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Provider</Label>
                      <Select value={aiConfig.secondary.provider} onValueChange={(v) => handleAiProviderChange('secondary', v)}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select provider…" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(aiProviders).map(([key, p]) => (
                            <SelectItem key={key} value={key}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {aiConfig.secondary.provider && (
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Model</Label>
                        <Select value={aiConfig.secondary.model} onValueChange={(v) => setAiConfig(prev => ({ ...prev, secondary: { ...prev.secondary, model: v } }))}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select model…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(aiProviders[aiConfig.secondary.provider]?.models || []).map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                <span className="flex items-center gap-2">
                                  {m.name}
                                  {m.supports_thinking && <Badge className="text-[10px] h-4 bg-violet-100 text-violet-700 border-0">thinking</Badge>}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {aiConfig.secondary.model && (
                      <p className="text-xs text-slate-400 font-mono bg-slate-50 rounded px-2 py-1">{aiConfig.secondary.model}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {aiProviders && (
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveAiConfig}
                  disabled={aiConfigSaving}
                  className="bg-violet-600 hover:bg-violet-700 text-white gap-2"
                >
                  {aiConfigSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
                  {aiConfigSaving ? 'Saving…' : 'Save AI Preferences'}
                </Button>
              </div>
            )}

            {/* ── Provider API Keys ── */}
            <div className="pt-2">
              <Separator className="mb-6" />
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-slate-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Provider API Keys</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Optional. If set, <strong>this client's keys are used first</strong> for all AI tasks
                (extractions, chat, scoring) — overriding the Consultancy workspace keys.
                Leave a field blank to keep the existing key unchanged.
                Clear a field and save to remove a key.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { provider: 'anthropic', label: 'Anthropic', hint: 'sk-ant-api03-…' },
                  { provider: 'openai',    label: 'OpenAI',    hint: 'sk-proj-…' },
                  { provider: 'gemini',    label: 'Google Gemini', hint: 'AIza…' },
                ].map(({ provider, label, hint }) => {
                  const val   = apiKeyInputs[provider] || '';
                  const isSet = val.startsWith('•') && val.length > 4;
                  const show  = apiKeysVisible[provider];
                  return (
                    <div key={provider} className="space-y-1.5">
                      <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                        {label}
                        {isSet && (
                          <Badge className="text-[10px] h-4 bg-emerald-100 text-emerald-700 border-0 font-medium">active</Badge>
                        )}
                      </Label>
                      <div className="relative">
                        <Input
                          type={show ? 'text' : 'password'}
                          value={val}
                          onChange={(e) => setApiKeyInputs(p => ({ ...p, [provider]: e.target.value }))}
                          placeholder={isSet ? 'Enter new key to replace' : hint}
                          className="pr-9 h-9 text-xs font-mono"
                        />
                        <button
                          type="button"
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                          onClick={() => setApiKeysVisible(p => ({ ...p, [provider]: !p[provider] }))}
                          tabIndex={-1}
                        >
                          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      {isSet && (
                        <button
                          type="button"
                          className="text-[11px] text-rose-500 hover:text-rose-700 underline"
                          onClick={() => setApiKeyInputs(p => ({ ...p, [provider]: '' }))}
                        >
                          Clear key
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={handleSaveApiKeys} disabled={apiKeysSaving}
                  variant="outline" className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-50">
                  {apiKeysSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  {apiKeysSaving ? 'Saving…' : 'Save API Keys'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ── 3rd Party Integrations Tab ── */}
          <TabsContent value="3rd-party" className="mt-6 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Link2 className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>3rd Party Integrations</h2>
              </div>
              <p className="text-xs text-slate-500">
                Connect external data sources to enrich this client's opportunity pipeline.
              </p>
            </div>

            {/* HigherGov card */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Link2 className="w-4 h-4 text-indigo-600" />
                  HigherGov
                  {hgConfig.searches.some(s => s.search_id.trim()) && hgConfig.api_key && (
                    <span className="ml-auto flex items-center gap-1 text-xs font-normal text-emerald-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                      Connected · {hgConfig.searches.filter(s => s.search_id.trim()).length} search{hgConfig.searches.filter(s => s.search_id.trim()).length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Pulls federal, state/local, and grant opportunities matching this client's saved search.
                </p>
              </CardHeader>
              <CardContent className="space-y-5">

                {/* API Key */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                    API Key <span className="text-red-500">*</span>
                    {hgConfig.api_key?.startsWith('•') && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">active</span>
                    )}
                  </Label>
                  <div className="relative">
                    <Input
                      type={hgApiKeyVisible ? 'text' : 'password'}
                      value={hgConfig.api_key}
                      onChange={(e) => setHgConfig(p => ({ ...p, api_key: e.target.value }))}
                      placeholder={hgConfig.api_key?.startsWith('•') ? 'Enter new key to replace' : 'Paste HigherGov API key…'}
                      className="pr-9 h-9 text-xs font-mono"
                    />
                    <button
                      type="button"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      onClick={() => setHgApiKeyVisible(v => !v)}
                      tabIndex={-1}
                    >
                      {hgApiKeyVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  {hgConfig.api_key?.startsWith('•') && (
                    <button
                      type="button"
                      className="text-[11px] text-rose-500 hover:text-rose-700 underline"
                      onClick={() => setHgConfig(p => ({ ...p, api_key: '' }))}
                    >
                      Clear key
                    </button>
                  )}
                </div>

                {/* Saved Searches */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                      <Hash className="w-3.5 h-3.5" />
                      Saved Searches <span className="text-red-500">*</span>
                    </Label>
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 font-medium"
                      onClick={() => setHgConfig(p => ({ ...p, searches: [...p.searches, { label: '', search_id: '', scope: '' }] }))}
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Search
                    </button>
                  </div>
                  <div className="space-y-2">
                    {hgConfig.searches.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex-1 grid grid-cols-7 gap-2">
                          <Input
                            value={entry.label}
                            onChange={(e) => setHgConfig(p => ({
                              ...p,
                              searches: p.searches.map((s, i) => i === idx ? { ...s, label: e.target.value } : s),
                            }))}
                            placeholder="Label (e.g. GIS Federal)"
                            className="h-8 text-xs col-span-2"
                          />
                          <Input
                            value={entry.search_id}
                            onChange={(e) => setHgConfig(p => ({
                              ...p,
                              searches: p.searches.map((s, i) => i === idx ? { ...s, search_id: e.target.value } : s),
                            }))}
                            placeholder="Search ID (e.g. aBNLLU30…)"
                            className="h-8 text-xs font-mono col-span-3"
                          />
                          <Select
                            value={entry.scope || 'both'}
                            onValueChange={(v) => setHgConfig(p => ({
                              ...p,
                              searches: p.searches.map((s, i) => i === idx ? { ...s, scope: v === 'both' ? '' : v } : s),
                            }))}
                          >
                            <SelectTrigger className="h-8 text-xs col-span-2" title="Limit this search to Federal or State/Local (SLED) opportunities only">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="both">Federal + SLED</SelectItem>
                              <SelectItem value="federal">Federal only</SelectItem>
                              <SelectItem value="sled">SLED only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <button
                          type="button"
                          className="text-slate-300 hover:text-rose-500 flex-shrink-0 disabled:opacity-30"
                          disabled={hgConfig.searches.length === 1}
                          onClick={() => setHgConfig(p => ({ ...p, searches: p.searches.filter((_, i) => i !== idx) }))}
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    From your saved search URL — e.g. <span className="font-mono">highergov.com/…/?searchID=aBNLL…</span>
                  </p>
                </div>

                <Separator className="bg-slate-100" />

                {/* Posted date range */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Posted Date Range — Days Back
                    <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={hgConfig.posted_days}
                      onChange={(e) => setHgConfig(p => ({ ...p, posted_days: e.target.value }))}
                      placeholder="30"
                      className="h-9 text-xs w-28"
                    />
                    <span className="text-xs text-slate-500">days</span>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Leave blank to use the search's default date range. Enter a number to limit results to opportunities posted within N days.
                  </p>
                </div>

                {/* NAICS codes */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5" />
                    NAICS Code Filter
                    <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    value={hgNaicsInput}
                    onChange={(e) => setHgNaicsInput(e.target.value)}
                    placeholder="541512, 541330, 517110"
                    className="h-9 text-xs font-mono"
                  />
                  <p className="text-[11px] text-slate-400">
                    Comma-separated NAICS codes to restrict results. Leave blank to accept all codes from the saved search.
                  </p>
                  {hgNaicsInput.trim() && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {hgNaicsInput.split(',').map(s => s.trim()).filter(Boolean).map((code, i) => (
                        <span key={i} className="text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-1.5 py-0.5 font-mono">
                          {code}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-1">
                  <Button
                    onClick={handleSaveHigherGov}
                    disabled={hgSaving}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                  >
                    {hgSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    {hgSaving ? 'Saving…' : 'Save HigherGov Config'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
        </div>
      </div>

      {/* Edit Client Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="edit-client-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Pencil className="w-5 h-5 text-blue-600" />
              Edit Client Profile
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Organization Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900 text-sm">Organization Information</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="edit-name">Company Name *</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name || ''}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    className="mt-1"
                    data-testid="edit-client-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-uei">UEI</Label>
                  <Input
                    id="edit-uei"
                    value={editForm.uei || ''}
                    onChange={(e) => handleEditChange('uei', e.target.value)}
                    className="mt-1 font-mono"
                    maxLength={12}
                    data-testid="edit-client-uei"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-cage">CAGE Code</Label>
                  <Input
                    id="edit-cage"
                    value={editForm.cage_code || ''}
                    onChange={(e) => handleEditChange('cage_code', e.target.value)}
                    className="mt-1 font-mono"
                    maxLength={5}
                    data-testid="edit-client-cage"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-naics">Primary NAICS</Label>
                  <Input
                    id="edit-naics"
                    value={editForm.primary_naics || ''}
                    onChange={(e) => handleEditChange('primary_naics', e.target.value)}
                    className="mt-1 font-mono"
                    maxLength={6}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-size">Business Size</Label>
                  <Input
                    id="edit-size"
                    value={editForm.business_size || ''}
                    onChange={(e) => handleEditChange('business_size', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-ebuy-code">eBuy Folder Code</Label>
                  <Input
                    id="edit-ebuy-code"
                    value={editForm.ebuy_client_code || ''}
                    onChange={(e) => handleEditChange('ebuy_client_code', e.target.value.toUpperCase().slice(0, 3))}
                    className="mt-1 font-mono uppercase"
                    maxLength={3}
                    placeholder="CEC"
                    data-testid="edit-client-ebuy-code"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    3-char code matching the eBuyExtract <code className="font-mono">scans/&lt;CODE&gt;/</code> folder for this client.
                  </p>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="edit-industry">Industry Sector</Label>
                  <Input
                    id="edit-industry"
                    value={editForm.industry_sector || ''}
                    onChange={(e) => handleEditChange('industry_sector', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900 text-sm">Contact Information</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="edit-website">
                    Website <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      id="edit-website"
                      value={editForm.website || ''}
                      onChange={(e) => handleEditChange('website', e.target.value)}
                      placeholder="https://example.com"
                      className={!(editForm.website || '').trim() ? 'border-red-400' : ''}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 gap-1.5 text-xs"
                      disabled={isScanning || !(editForm.website || '').trim()}
                      onClick={handleScanWebsite}
                      title="AI scans the website to extract brand colors and capabilities"
                    >
                      {isScanning
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5 text-amber-500" />}
                      {isScanning ? 'Scanning…' : 'Analyze'}
                    </Button>
                  </div>
                  {!(editForm.website || '').trim() && (
                    <p className="text-xs text-red-500 mt-1">Website URL is required</p>
                  )}
                </div>

                {/* AI brand scan results — shown once colors are available */}
                {editForm.brand_colors?.primary && (
                  <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Palette className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">
                        Brand Palette{editForm.brand_colors.palette_name ? ` — ${editForm.brand_colors.palette_name}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {['primary','secondary','accent','background','text'].map(key => {
                        const hex = editForm.brand_colors?.[key];
                        if (!hex) return null;
                        return (
                          <div key={key} className="flex flex-col items-center gap-1">
                            <div
                              className="w-9 h-9 rounded-md border border-slate-200 shadow-sm"
                              style={{ background: hex }}
                              title={hex}
                            />
                            <span className="text-[10px] text-slate-500 uppercase">{key}</span>
                            <span className="text-[10px] text-slate-400 font-mono">{hex}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Capabilities summary — always visible, auto-populated by Analyze */}
                <div className="col-span-2">
                  <Label htmlFor="edit-capabilities">Capabilities Summary</Label>
                  <Textarea
                    id="edit-capabilities"
                    rows={4}
                    value={editForm.capabilities_summary || ''}
                    onChange={(e) => handleEditChange('capabilities_summary', e.target.value)}
                    placeholder="Brief description of what this client does and their key capabilities. Click Analyze to auto-populate from their website, or type manually."
                    className="mt-1 text-sm resize-none"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editForm.phone || ''}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Address */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900 text-sm">Address</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="edit-address1">Street Address</Label>
                  <Input
                    id="edit-address1"
                    value={editForm.address_line1 || ''}
                    onChange={(e) => handleEditChange('address_line1', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="edit-address2">Address Line 2</Label>
                  <Input
                    id="edit-address2"
                    value={editForm.address_line2 || ''}
                    onChange={(e) => handleEditChange('address_line2', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-city">City</Label>
                  <Input
                    id="edit-city"
                    value={editForm.city || ''}
                    onChange={(e) => handleEditChange('city', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="edit-state">State</Label>
                    <Input
                      id="edit-state"
                      value={editForm.state || ''}
                      onChange={(e) => handleEditChange('state', e.target.value)}
                      className="mt-1"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-zip">ZIP</Label>
                    <Input
                      id="edit-zip"
                      value={editForm.zip_code || ''}
                      onChange={(e) => handleEditChange('zip_code', e.target.value)}
                      className="mt-1"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!editForm.name || !(editForm.website || '').trim() || isSaving}
              onClick={handleSaveClient}
              data-testid="save-client-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.11.27 — CRUD parity modals for legacy KB cards.
          The `key` prop forces a clean remount whenever the editing target
          changes, which guarantees the modal's local form state is freshly
          seeded from `initial` (avoids stale-state on rapid edit-click). */}
      <CertificationFormModal
        key={`cert-${certEditing?.id || 'new'}`}
        open={certModalOpen}
        onOpenChange={(o) => { setCertModalOpen(o); if (!o) setCertEditing(null); }}
        organizationId={clientId}
        initial={certEditing}
        onSaved={() => fetchClientData()}
      />
      <KeyPersonnelFormModal
        key={`kp-${kpEditing?.id || 'new'}`}
        open={kpModalOpen}
        onOpenChange={(o) => { setKpModalOpen(o); if (!o) setKpEditing(null); }}
        organizationId={clientId}
        initial={kpEditing}
        onSaved={() => fetchClientData()}
      />
      <PastPerformanceFormModal
        key={`pp-${ppEditing?.id || 'new'}`}
        open={ppModalOpen}
        onOpenChange={(o) => { setPpModalOpen(o); if (!o) setPpEditing(null); }}
        organizationId={clientId}
        initial={ppEditing}
        onSaved={() => fetchClientData()}
      />

      {/* Certification Detail Dialog */}
      <Dialog open={certDetailOpen} onOpenChange={setCertDetailOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="truncate">{certDetailItem?.name || 'Certification'}</span>
            </DialogTitle>
          </DialogHeader>
          {certDetailItem && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className={cn("text-xs", certDetailItem.status === 'ACTIVE' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : certDetailItem.status === 'EXPIRED' ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700')}>
                  {certDetailItem.status || 'PENDING'}
                </Badge>
                {certDetailItem.category && <Badge variant="outline" className="text-xs text-slate-600">{certDetailItem.category}</Badge>}
                <ExtractionStatusBadge status={certDetailItem.extraction_status} error={certDetailItem.extraction_error} />
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {certDetailItem.issuing_body && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Issuing Body</p><p className="text-sm text-slate-900">{certDetailItem.issuing_body}</p></div>
                )}
                {certDetailItem.certificate_number && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Certificate #</p><p className="text-sm font-mono text-slate-900">{certDetailItem.certificate_number}</p></div>
                )}
                {certDetailItem.issue_date && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Issue Date</p><p className="text-sm text-slate-900">{new Date(certDetailItem.issue_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p></div>
                )}
                {certDetailItem.expiration_date && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Expiration</p><p className={cn("text-sm", new Date(certDetailItem.expiration_date) < new Date() ? 'text-red-600 font-medium' : 'text-slate-900')}>{new Date(certDetailItem.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p></div>
                )}
              </div>
              {certDetailItem.scope && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Scope</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{certDetailItem.scope}</p></div>}
              {certDetailItem.notes && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Notes</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{certDetailItem.notes}</p></div>}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Uploaded Files</p>
                {certDetailLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><Loader2 className="w-4 h-4 animate-spin" />Loading files…</div>
                ) : certDetailAttachments.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No files uploaded yet</p>
                ) : (
                  <div className="space-y-2">
                    {certDetailAttachments.map((att) => (
                      <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{att.filename}</p>
                          {att.file_size && <p className="text-xs text-slate-400">{att.file_size > 1048576 ? `${(att.file_size / 1048576).toFixed(1)} MB` : `${Math.round(att.file_size / 1024)} KB`}</p>}
                        </div>
                        <Button variant="outline" size="sm" className="shrink-0 h-8 gap-1.5 text-xs" onClick={() => downloadKbAttachment(att)}><Download className="w-3.5 h-3.5" />Download</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => { setCertDetailOpen(false); setCertEditing(certDetailItem); setCertModalOpen(true); }}><Pencil className="w-4 h-4 mr-2" />Edit</Button>
            <Button variant="outline" onClick={() => setCertDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Personnel Detail Dialog */}
      <Dialog open={kpDetailOpen} onOpenChange={setKpDetailOpen}>
        <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Users className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="truncate">{kpDetailItem ? `${kpDetailItem.first_name} ${kpDetailItem.last_name}` : 'Team Member'}</span>
            </DialogTitle>
          </DialogHeader>
          {kpDetailItem && (
            <div className="space-y-5 py-2">
              <div className="flex items-center gap-3 flex-wrap">
                {kpDetailItem.title && <span className="text-sm font-medium text-slate-700">{kpDetailItem.title}</span>}
                {kpDetailItem.labor_category && <Badge variant="outline" className="text-xs text-slate-600">{kpDetailItem.labor_category}</Badge>}
                {kpDetailItem.clearance_level && kpDetailItem.clearance_level !== 'NONE' && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-xs"><Shield className="w-3 h-3 mr-1" />{kpDetailItem.clearance_level.replace('_', ' ')}</Badge>
                )}
                {kpDetailItem.availability && <Badge variant="outline" className={cn("text-xs", kpDetailItem.availability === 'AVAILABLE' ? 'border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-500')}>{kpDetailItem.availability}</Badge>}
                <ExtractionStatusBadge status={kpDetailItem.extraction_status} error={kpDetailItem.extraction_error} />
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {kpDetailItem.years_experience != null && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Experience</p><p className="text-sm text-slate-900">{kpDetailItem.years_experience} years</p></div>
                )}
                {kpDetailItem.email && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Email</p><a href={`mailto:${kpDetailItem.email}`} className="text-sm text-blue-600 hover:underline truncate block">{kpDetailItem.email}</a></div>
                )}
                {kpDetailItem.phone && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Phone</p><p className="text-sm text-slate-900">{kpDetailItem.phone}</p></div>
                )}
                {kpDetailItem.current_assignment && (
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Current Assignment</p><p className="text-sm text-slate-900">{kpDetailItem.current_assignment}</p></div>
                )}
              </div>
              {kpDetailItem.bio && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Bio</p><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{kpDetailItem.bio}</p></div>}
              {kpDetailItem.education?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Education</p>
                  <div className="space-y-1">
                    {kpDetailItem.education.map((edu, i) => (
                      <p key={i} className="text-sm text-slate-700">{edu.degree}{edu.field ? ` in ${edu.field}` : ''} — {edu.institution}{edu.year ? `, ${edu.year}` : ''}</p>
                    ))}
                  </div>
                </div>
              )}
              {kpDetailItem.professional_certifications?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Certifications</p>
                  <div className="flex flex-wrap gap-1.5">
                    {kpDetailItem.professional_certifications.map((c, i) => <Badge key={i} className="bg-green-50 text-green-700 border border-green-200 text-xs">{c}</Badge>)}
                  </div>
                </div>
              )}
              {kpDetailItem.technical_skills?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Technical Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {kpDetailItem.technical_skills.map((s, i) => <Badge key={i} variant="outline" className="text-xs text-slate-600">{s}</Badge>)}
                  </div>
                </div>
              )}
              {kpDetailItem.specializations?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Specializations</p>
                  <div className="flex flex-wrap gap-1.5">
                    {kpDetailItem.specializations.map((s, i) => <Badge key={i} variant="outline" className="text-xs text-slate-600">{s}</Badge>)}
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Uploaded Files</p>
                {kpDetailLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><Loader2 className="w-4 h-4 animate-spin" />Loading files…</div>
                ) : kpDetailAttachments.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No files uploaded yet</p>
                ) : (
                  <div className="space-y-2">
                    {kpDetailAttachments.map((att) => (
                      <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{att.filename}</p>
                          {att.file_size && <p className="text-xs text-slate-400">{att.file_size > 1048576 ? `${(att.file_size / 1048576).toFixed(1)} MB` : `${Math.round(att.file_size / 1024)} KB`}</p>}
                        </div>
                        <Button variant="outline" size="sm" className="shrink-0 h-8 gap-1.5 text-xs" onClick={() => downloadKbAttachment(att)}><Download className="w-3.5 h-3.5" />Download</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => { setKpDetailOpen(false); setKpEditing(kpDetailItem); setKpModalOpen(true); }}><Pencil className="w-4 h-4 mr-2" />Edit</Button>
            <Button variant="outline" onClick={() => setKpDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Past Performance Detail Dialog */}
      <Dialog open={ppDetailOpen} onOpenChange={setPpDetailOpen}>
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Briefcase className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="truncate">{ppDetailItem?.contract_title || 'Past Performance'}</span>
            </DialogTitle>
          </DialogHeader>

          {ppDetailItem && (
            <div className="space-y-5 py-2">
              {/* Header badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {ppDetailItem.client_agency && (
                  <span className="text-sm font-semibold text-slate-700">{ppDetailItem.client_agency}</span>
                )}
                {ppDetailItem.client_office && (
                  <span className="text-xs text-slate-500">/ {ppDetailItem.client_office}</span>
                )}
                {ppDetailItem.cpars_rating && (
                  <Badge className={cn("text-xs ml-1", getRatingBadgeStyle(ppDetailItem.cpars_rating))}>
                    CPARS: {formatCparsRating(ppDetailItem.cpars_rating)}
                  </Badge>
                )}
                {ppDetailItem.contract_type && (
                  <Badge variant="outline" className="text-xs text-slate-600">{ppDetailItem.contract_type}</Badge>
                )}
                <Badge variant="outline" className={cn("text-xs", ppDetailItem.is_ongoing ? 'border-emerald-200 text-emerald-600' : 'border-slate-200 text-slate-500')}>
                  {ppDetailItem.is_ongoing ? 'Active' : 'Completed'}
                </Badge>
                {ppDetailItem.prime_contractor && (
                  <Badge variant="outline" className="text-xs text-purple-700 border-purple-200 bg-purple-50">
                    {ppDetailItem.role_on_contract || 'Sub'} — {ppDetailItem.prime_contractor}
                  </Badge>
                )}
                <ExtractionStatusBadge status={ppDetailItem.extraction_status} error={ppDetailItem.extraction_error} />
              </div>

              {/* Contract IDs + financials + PoP grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {ppDetailItem.contract_number && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Contract Number</p>
                    <p className="text-sm font-mono text-slate-900">{ppDetailItem.contract_number}</p>
                  </div>
                )}
                {ppDetailItem.task_order_number && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Task Order Number</p>
                    <p className="text-sm font-mono text-slate-900">{ppDetailItem.task_order_number}</p>
                  </div>
                )}
                {ppDetailItem.contract_value != null && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Contract Value</p>
                    <p className="text-sm font-semibold text-slate-900">{formatCurrency(ppDetailItem.contract_value)}</p>
                  </div>
                )}
                {ppDetailItem.funded_value != null && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Funded Value</p>
                    <p className="text-sm text-slate-900">{formatCurrency(ppDetailItem.funded_value)}</p>
                  </div>
                )}
                {(ppDetailItem.pop_start || ppDetailItem.pop_end) && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Period of Performance</p>
                    <p className="text-sm text-slate-900">
                      {ppDetailItem.pop_start ? new Date(ppDetailItem.pop_start).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '?'}
                      {' — '}
                      {ppDetailItem.is_ongoing ? <span className="text-emerald-600 font-medium">Present (Active)</span> : ppDetailItem.pop_end ? new Date(ppDetailItem.pop_end).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '?'}
                    </p>
                  </div>
                )}
              </div>

              {/* NAICS / PSC / Tags row */}
              {((ppDetailItem.naics_codes?.length > 0) || (ppDetailItem.psc_codes?.length > 0) || (ppDetailItem.relevance_tags?.length > 0)) && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-100">
                  {ppDetailItem.naics_codes?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-16 shrink-0">NAICS</span>
                      {ppDetailItem.naics_codes.map(c => (
                        <Badge key={c} variant="outline" className="text-xs font-mono text-slate-700">{c}</Badge>
                      ))}
                    </div>
                  )}
                  {ppDetailItem.psc_codes?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-16 shrink-0">PSC</span>
                      {ppDetailItem.psc_codes.map(c => (
                        <Badge key={c} variant="outline" className="text-xs font-mono text-slate-700">{c}</Badge>
                      ))}
                    </div>
                  )}
                  {ppDetailItem.relevance_tags?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-16 shrink-0">Tags</span>
                      {ppDetailItem.relevance_tags.map(t => (
                        <Badge key={t} className="text-xs bg-blue-50 text-blue-700 border border-blue-200">{t}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              {ppDetailItem.description && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Description</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{ppDetailItem.description}</p>
                </div>
              )}

              {/* Key Accomplishments */}
              {ppDetailItem.key_accomplishments?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Key Accomplishments</p>
                  <ul className="space-y-1">
                    {ppDetailItem.key_accomplishments.map((acc, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{acc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CPARS Narrative */}
              {ppDetailItem.cpars_narrative && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">CPARS Narrative</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-100">{ppDetailItem.cpars_narrative}</p>
                </div>
              )}

              {/* Contracting Officer / POC */}
              {(ppDetailItem.poc_name || ppDetailItem.poc_email || ppDetailItem.poc_phone) && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Point of Contact</p>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                    {ppDetailItem.poc_name && (
                      <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                        <UserCheck className="w-3.5 h-3.5 text-slate-400" />
                        {ppDetailItem.poc_name}
                        {ppDetailItem.poc_title && <span className="text-slate-500 font-normal">— {ppDetailItem.poc_title}</span>}
                      </p>
                    )}
                    {ppDetailItem.poc_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        <a href={`mailto:${ppDetailItem.poc_email}`} className="hover:text-blue-600 hover:underline">{ppDetailItem.poc_email}</a>
                      </p>
                    )}
                    {ppDetailItem.poc_phone && (
                      <p className="text-sm text-slate-600 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        {ppDetailItem.poc_phone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Uploaded files */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Uploaded Files</p>
                {ppAttachmentsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading files…
                  </div>
                ) : ppAttachments.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No files uploaded yet</p>
                ) : (
                  <div className="space-y-2">
                    {ppAttachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                      >
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{att.filename}</p>
                          {att.file_size && (
                            <p className="text-xs text-slate-400">
                              {att.file_size > 1048576
                                ? `${(att.file_size / 1048576).toFixed(1)} MB`
                                : `${Math.round(att.file_size / 1024)} KB`}
                              {att.content_type && ` · ${att.content_type}`}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 h-8 gap-1.5 text-xs"
                          onClick={() => downloadPpAttachment(att)}
                          title="Download file"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setPpDetailOpen(false);
                setPpEditing(ppDetailItem);
                setPpModalOpen(true);
              }}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" onClick={() => setPpDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
